import React, { useEffect, useState } from "react";
import "./SaveBar.css";

// Always-visible save bar for the website-content editors (Website Menu,
// About Our Fabric, Social Links), with a pop-up that walks through saving:
//   confirm → saving… → saved ✓  (or the problem, with the edits kept)
//
// Props:
//   dirty, saving      – current state from the page
//   onSave()           – saves; returns { ok: true } or { ok: false, message }
//   onDiscard()        – throws away unsaved edits
//   check()            – optional; returns a problem message to fix first, or null
//   what               – what is being saved, e.g. "the website menu"
const SaveBar = ({ dirty, saving, onSave, onDiscard, check, what = "your changes", savedText = "All changes saved" }) => {
  const [popup, setPopup] = useState(null); // { phase: confirm | saving | done | blocked | failed, message }

  const startSave = () => {
    const problem = check ? check() : null;
    setPopup(problem ? { phase: "blocked", message: problem } : { phase: "confirm" });
  };

  const confirmSave = async () => {
    setPopup({ phase: "saving" });
    const result = (await onSave()) || { ok: false, message: "Something went wrong" };
    setPopup(result.ok ? { phase: "done" } : { phase: "failed", message: result.message });
  };

  // the success message closes by itself
  useEffect(() => {
    if (popup?.phase !== "done") return;
    const t = setTimeout(() => setPopup(null), 2200);
    return () => clearTimeout(t);
  }, [popup]);

  // Esc closes the pop-up (not while saving)
  useEffect(() => {
    if (!popup || popup.phase === "saving") return;
    const onKey = (e) => e.key === "Escape" && setPopup(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [popup]);

  const close = () => popup?.phase !== "saving" && setPopup(null);

  return (
    <>
      {/* keeps the last field on the page from sitting under the bar */}
      <div className="savebar-spacer" aria-hidden="true" />
      <div className={`savebar ${dirty ? "is-dirty" : "is-clean"}`} role="region" aria-label="Save changes bar">
        <span className="savebar-text">
          {saving ? (
            <><i className="savebar-spinner" /> Saving…</>
          ) : dirty ? (
            <><i className="savebar-dot" /> You have unsaved changes</>
          ) : (
            <><i className="savebar-tick">✓</i> {savedText}</>
          )}
        </span>
        <div className="savebar-actions">
          <button type="button" className="savebar-btn ghost" onClick={onDiscard} disabled={!dirty || saving}>Discard</button>
          <button type="button" className="savebar-btn primary" onClick={startSave} disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {popup && (
        <div className="savepop-overlay" onClick={close}>
          <div className={`savepop phase-${popup.phase}`} role="dialog" aria-modal="true" aria-label="Saving"
            onClick={(e) => e.stopPropagation()}>
            {popup.phase === "confirm" && (
              <>
                <div className="savepop-icon info">💾</div>
                <h3>Save your changes?</h3>
                <p>The changes to {what} will show on the website straight away.</p>
                <div className="savepop-actions">
                  <button type="button" className="savebar-btn light" onClick={close}>Cancel</button>
                  <button type="button" className="savebar-btn primary" onClick={confirmSave} autoFocus>Yes, save</button>
                </div>
              </>
            )}

            {popup.phase === "saving" && (
              <>
                <div className="savepop-icon"><i className="savepop-spinner" /></div>
                <h3>Saving your changes…</h3>
                <p>Please wait a moment and don't close this page.</p>
                <div className="savepop-progress"><span /></div>
              </>
            )}

            {popup.phase === "done" && (
              <>
                <div className="savepop-icon ok">✓</div>
                <h3>Saved!</h3>
                <p>Your changes are now live on the website.</p>
                <div className="savepop-actions center">
                  <button type="button" className="savebar-btn primary" onClick={close} autoFocus>Done</button>
                </div>
              </>
            )}

            {(popup.phase === "blocked" || popup.phase === "failed") && (
              <>
                <div className="savepop-icon error">!</div>
                <h3>{popup.phase === "blocked" ? "Please fix this first" : "Couldn't save"}</h3>
                <p className="savepop-message">{popup.message}</p>
                <p className="savepop-note">Nothing was lost — your edits are still on the page.</p>
                <div className="savepop-actions center">
                  {popup.phase === "failed" && (
                    <button type="button" className="savebar-btn light" onClick={confirmSave}>Try again</button>
                  )}
                  <button type="button" className="savebar-btn primary" onClick={close} autoFocus>OK</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SaveBar;
